import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar, Sparkles, Save, RefreshCw, X, UtensilsCrossed, Coffee, Soup, RotateCcw, CakeSlice, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AIMealPlanDialog from "@/components/AIMealPlanDialog";

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_NAMES = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];
const MEALS = [
  { id: "breakfast", label: "Frokost", Icon: Coffee, accent: "bg-amber-50 text-amber-800 border-amber-100" },
  { id: "lunch",     label: "Lunsj",    Icon: Soup,   accent: "bg-emerald-50 text-emerald-800 border-emerald-100" },
  { id: "dinner",    label: "Middag",   Icon: UtensilsCrossed, accent: "bg-orange-50 text-orange-800 border-orange-100" },
];

function getMondayOfWeek(d = new Date()) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff)).toISOString().slice(0, 10);
}

function addDays(isoDate, n) {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + n);
  return d;
}

function formatDateShort(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${day}.${m}`;
}

export default function HouseholdPlan() {
  const navigate = useNavigate();
  const { activeBoat } = useAuth();
  const [weekStart] = useState(() => getMondayOfWeek());
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDay, setPickerDay] = useState(null);
  const [pickerMeal, setPickerMeal] = useState(null);
  const [customText, setCustomText] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [bakingPlan, setBakingPlan] = useState(null);
  const [bakingRecipes, setBakingRecipes] = useState([]);

  const days = activeBoat?.household_days || 7;

  const load = useCallback(async () => {
    if (!activeBoat) return;
    setLoading(true);
    try {
      const [planRes, recipesRes, bakingRes, bakingRecipesRes] = await Promise.all([
        api.getMealPlan(weekStart, "1"),
        api.getRecipes().catch(() => ({ data: [] })),
        api.getBakingPlan(weekStart).catch(() => ({ data: { items: [] } })),
        api.getRecipes(true).catch(() => ({ data: [] })),
      ]);
      setPlan(planRes.data);
      setRecipes((recipesRes.data || []).filter((r) => !r.is_baking));
      setBakingPlan(bakingRes.data);
      setBakingRecipes(bakingRecipesRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke laste matplan");
    } finally { setLoading(false); }
  }, [activeBoat, weekStart]);

  useEffect(() => { load(); }, [load]);

  const openPicker = (day, meal) => {
    setPickerDay(day);
    setPickerMeal(meal);
    setCustomText("");
    setPickerOpen(true);
  };

  const setSlot = (day, meal, slot) => {
    setPlan((p) => ({
      ...p,
      [day]: { ...(p?.[day] || {}), [meal]: slot },
    }));
    setHasChanges(true);
  };

  const assignRecipe = (recipe) => {
    if (!pickerDay || !pickerMeal) return;
    setSlot(pickerDay, pickerMeal, { recipe_id: recipe.id, recipe_title: recipe.title, custom_meal: "" });
    setPickerOpen(false);
    toast.success(`${recipe.title} lagt til ${DAY_NAMES[DAY_KEYS.indexOf(pickerDay)]}`);
  };

  const assignCustom = () => {
    if (!pickerDay || !pickerMeal || !customText.trim()) return;
    setSlot(pickerDay, pickerMeal, { recipe_id: null, recipe_title: "", custom_meal: customText.trim() });
    setPickerOpen(false);
    setCustomText("");
  };

  const clearSlot = (day, meal) => {
    setSlot(day, meal, { recipe_id: null, recipe_title: "", custom_meal: "" });
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      await api.updateMealPlan(weekStart, plan, "1");
      setHasChanges(false);
      toast.success("Matplan lagret");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre");
    } finally { setSaving(false); }
  };

  const resetPlan = async () => {
    if (!window.confirm("Nullstill hele matplanen for denne uken?")) return;
    const empty = { week_start: weekStart };
    DAY_KEYS.forEach((d) => { empty[d] = { breakfast: {}, lunch: {}, dinner: {}, dessert: {}, extras: {}, extra_labels: {} }; });
    setPlan(empty);
    setHasChanges(true);
    toast.info("Matplan nullstilt. Husk å lagre.");
  };

  const applyAI = async (sugg) => {
    if (!sugg) return;
    const dks = Object.keys(sugg);
    const updated = { ...plan };
    DAY_KEYS.forEach((d) => { if (!updated[d]) updated[d] = { breakfast: {}, lunch: {}, dinner: {}, dessert: {}, extras: {}, extra_labels: {} }; });
    let filled = 0;
    for (let i = 0; i < Math.min(dks.length, DAY_KEYS.length); i++) {
      const day = DAY_KEYS[i];
      const ds = sugg[dks[i]] || {};
      for (const m of ["breakfast", "lunch", "dinner"]) {
        if (!ds[m]) continue;
        const rid = ds[m].recipe_id;
        const title = ds[m].recipe_title || "";
        const cm = ds[m].custom_meal || "";
        if (!rid || rid === "custom_meal" || rid === "null" || rid === "") {
          updated[day][m] = { recipe_id: null, recipe_title: "", custom_meal: cm || title || "" };
        } else {
          updated[day][m] = { recipe_id: rid, recipe_title: title, custom_meal: "" };
        }
        filled++;
      }
    }
    setPlan(updated);
    setHasChanges(true);
    toast.success(`${filled} måltider lagt inn. Husk å lagre.`);
  };

  const handlePrint = () => {
    const householdName = activeBoat?.name || "Husstand";
    const startDate = new Date(weekStart);
    const endDate = addDays(weekStart, Math.min(days, 7) - 1);
    const rangeStr = `${formatDateShort(startDate)} – ${formatDateShort(endDate)}`;
    const printWin = window.open("", "_blank");
    if (!printWin) {
      toast.error("Pop-up blokkert — tillat popup og prøv igjen");
      return;
    }
    const doc = printWin.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="nb"><head><meta charset="utf-8"><title>Matplan — ${householdName}</title>
      <style>
        body { font-family: 'Segoe UI', system-ui, sans-serif; padding: 32px; max-width: 780px; margin: 0 auto; color: #1c1917; }
        h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 32px; margin: 0 0 4px; font-weight: 600; }
        .subtitle { color: #78716c; font-size: 13px; margin-bottom: 28px; }
        .day { border: 1px solid #e7e5e4; border-radius: 12px; padding: 14px 18px; margin-bottom: 14px; break-inside: avoid; page-break-inside: avoid; }
        .day-header { display: flex; align-items: baseline; gap: 10px; border-bottom: 1px solid #f5f5f4; padding-bottom: 8px; margin-bottom: 10px; }
        .day-name { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 22px; font-weight: 600; color: #292524; }
        .day-date { font-size: 12px; color: #a8a29e; }
        .meal-row { display: flex; gap: 12px; padding: 6px 0; font-size: 14px; align-items: baseline; }
        .meal-label { flex: 0 0 86px; font-size: 10px; font-weight: 700; color: #78716c; text-transform: uppercase; letter-spacing: 1.2px; }
        .meal-value { flex: 1; color: #292524; }
        .meal-empty { color: #d6d3d1; font-style: italic; }
        .baking-chip { display: inline-block; background: #fff1f2; border: 1px solid #fecdd3; color: #9f1239; padding: 2px 8px; border-radius: 10px; margin-right: 4px; margin-bottom: 2px; font-size: 12px; }
        .footer { margin-top: 32px; text-align: center; color: #d6d3d1; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; }
        @media print { body { padding: 18px; } .day { break-inside: avoid; } }
      </style></head><body></body></html>`);
    doc.close();
    const body = doc.body;

    const h1 = doc.createElement("h1");
    h1.textContent = `Matplan — ${householdName}`;
    body.appendChild(h1);
    const sub = doc.createElement("div");
    sub.className = "subtitle";
    sub.textContent = `${rangeStr} · ${Math.min(days, 7)} dager · inkl. ukens bakst`;
    body.appendChild(sub);

    for (let i = 0; i < Math.min(days, 7); i++) {
      const dayKey = DAY_KEYS[i];
      const dayDate = addDays(weekStart, i);
      const dayName = DAY_NAMES[i];
      const dayPlan = plan?.[dayKey] || {};
      const bakingItems = (bakingPlan?.items || []).filter((it) => it.day === dayKey);

      const dayCard = doc.createElement("div");
      dayCard.className = "day";

      const hdr = doc.createElement("div");
      hdr.className = "day-header";
      const dn = doc.createElement("span"); dn.className = "day-name"; dn.textContent = dayName;
      const dd = doc.createElement("span"); dd.className = "day-date"; dd.textContent = formatDateShort(dayDate);
      hdr.appendChild(dn); hdr.appendChild(dd);
      dayCard.appendChild(hdr);

      // Meals
      for (const m of MEALS) {
        const slot = dayPlan[m.id] || {};
        const text = (slot.recipe_title || slot.custom_meal || "").trim();
        const row = doc.createElement("div");
        row.className = "meal-row";
        const lab = doc.createElement("span"); lab.className = "meal-label"; lab.textContent = m.label;
        const val = doc.createElement("span");
        val.className = text ? "meal-value" : "meal-value meal-empty";
        val.textContent = text || "—";
        row.appendChild(lab); row.appendChild(val);
        dayCard.appendChild(row);
      }

      // Baking row
      const bakingRow = doc.createElement("div");
      bakingRow.className = "meal-row";
      const bLab = doc.createElement("span"); bLab.className = "meal-label"; bLab.textContent = "Bakst";
      const bVal = doc.createElement("span");
      bVal.className = bakingItems.length ? "meal-value" : "meal-value meal-empty";
      if (bakingItems.length === 0) {
        bVal.textContent = "—";
      } else {
        for (const bi of bakingItems) {
          const chip = doc.createElement("span");
          chip.className = "baking-chip";
          chip.textContent = bi.custom_name || bi.recipe_title || "Bakst";
          bVal.appendChild(chip);
        }
      }
      bakingRow.appendChild(bLab); bakingRow.appendChild(bVal);
      dayCard.appendChild(bakingRow);

      body.appendChild(dayCard);
    }

    const footer = doc.createElement("div");
    footer.className = "footer";
    footer.textContent = `PantryBuddy Home Edition · Generert ${new Date().toLocaleDateString("nb-NO")}`;
    body.appendChild(footer);

    // Give browser a tick to render before printing
    setTimeout(() => { try { printWin.print(); } catch (e) { console.warn(e); } }, 250);
  };

  if (!activeBoat) {
    return <div className="p-6 text-center text-sm text-stone-500">Ingen husstand valgt.</div>;
  }
  if (activeBoat.kind !== "household") {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center">
        <p className="text-sm text-stone-500">Denne siden er for Home Edition. <button onClick={() => navigate("/")} className="underline text-navy-800">Gå til Turnusplan</button></p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto pb-24" data-testid="household-plan-page">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Matplan</h1>
          <p className="text-sm text-stone-500 mt-1 flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" /> {days} dager fra mandag {formatDateShort(new Date(weekStart))} · inkl. ukens bakst
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setAiOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-orange-600 to-red-600 text-white px-3 py-2 text-xs font-semibold hover:from-orange-700 hover:to-red-700"
            data-testid="household-ai-btn"
          >
            <Sparkles className="h-3.5 w-3.5" /> AI matplan
          </button>
          <Link
            to="/baking"
            className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 text-rose-700 bg-rose-50 px-3 py-2 text-xs font-semibold hover:bg-rose-100"
            data-testid="household-baking-link"
          >
            <CakeSlice className="h-3.5 w-3.5" /> Bakeplan
          </Link>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 text-stone-700 bg-white px-3 py-2 text-xs font-semibold hover:bg-stone-50"
            data-testid="household-print-btn"
            title="Skriv ut eller lagre som PDF"
          >
            <Printer className="h-3.5 w-3.5" /> Skriv ut
          </button>
          <button
            onClick={resetPlan}
            className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 text-stone-600 px-3 py-2 text-xs font-semibold hover:bg-stone-50"
            data-testid="household-reset-btn"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Nullstill
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-stone-400">Laster...</div>
      ) : (
        <div className="space-y-2" data-testid="day-list">
          {Array.from({ length: Math.min(days, 7) }, (_, i) => {
            const dayKey = DAY_KEYS[i];
            const date = addDays(weekStart, i);
            const dayName = DAY_NAMES[i];
            const dayPlan = plan?.[dayKey] || {};
            return (
              <div key={dayKey} className="bg-white border border-stone-100 rounded-xl overflow-hidden" data-testid={`day-${dayKey}`}>
                <div className="flex items-baseline gap-2 px-4 pt-3 pb-2 border-b border-stone-50">
                  <span className="text-sm font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{dayName}</span>
                  <span className="text-xs text-stone-400">{formatDateShort(date)}</span>
                </div>
                <div className="divide-y divide-stone-50">
                  {MEALS.map((m) => {
                    const Icon = m.Icon;
                    const slot = dayPlan[m.id] || {};
                    const label = slot.recipe_title || slot.custom_meal || "";
                    const hasValue = !!label;
                    return (
                      <div key={m.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`${dayKey}-${m.id}`}>
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold flex-shrink-0 ${m.accent}`}>
                          <Icon className="h-3 w-3" />
                          {m.label}
                        </span>
                        <button
                          onClick={() => openPicker(dayKey, m.id)}
                          className={`flex-1 min-w-0 text-left text-sm rounded-md px-2 py-1 transition-colors ${hasValue ? "text-stone-800 hover:bg-stone-50" : "text-stone-400 italic hover:bg-stone-50"}`}
                          data-testid={`slot-${dayKey}-${m.id}`}
                        >
                          {hasValue ? label : "+ Velg eller skriv fritekst"}
                        </button>
                        {hasValue && (
                          <button
                            onClick={() => clearSlot(dayKey, m.id)}
                            className="p-1 text-stone-300 hover:text-red-500 flex-shrink-0"
                            data-testid={`clear-${dayKey}-${m.id}`}
                            title="Fjern"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {/* Baking row — read-only strip with link to BakingSection */}
                  {(() => {
                    const bakingItems = (bakingPlan?.items || []).filter((i) => i.day === dayKey);
                    if (bakingItems.length === 0 && !hasChanges) {
                      // Still render a muted placeholder so users know they can add baking
                      return (
                        <div className="flex items-center gap-3 px-4 py-2.5" data-testid={`${dayKey}-baking-empty`}>
                          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold flex-shrink-0 bg-rose-50 text-rose-700 border-rose-100">
                            <CakeSlice className="h-3 w-3" />
                            Bakst
                          </span>
                          <Link
                            to="/baking"
                            className="flex-1 min-w-0 text-left text-sm rounded-md px-2 py-1 text-stone-400 italic hover:bg-stone-50 transition-colors"
                            data-testid={`baking-add-${dayKey}`}
                          >
                            + Legg til bakst (åpner bakeplanen)
                          </Link>
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-start gap-3 px-4 py-2.5" data-testid={`${dayKey}-baking`}>
                        <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold flex-shrink-0 bg-rose-50 text-rose-700 border-rose-100 mt-0.5">
                          <CakeSlice className="h-3 w-3" />
                          Bakst
                        </span>
                        <div className="flex-1 min-w-0 flex flex-wrap gap-1.5">
                          {bakingItems.map((bi) => {
                            const name = bi.custom_name || bi.recipe_title || "Ukjent bakst";
                            // Fuzzy-match to recipe so chip is clickable
                            let rid = bi.recipe_id || null;
                            if (!rid && bi.custom_name) {
                              const needle = bi.custom_name.trim().toLowerCase();
                              const match = bakingRecipes.find((r) => r.title && r.title.trim().toLowerCase() === needle)
                                || bakingRecipes.find((r) => r.title && r.title.toLowerCase().includes(needle));
                              if (match) rid = match.id;
                            }
                            if (rid) {
                              return (
                                <Link
                                  key={bi.id}
                                  to={`/recipes/${rid}`}
                                  className="inline-flex items-center gap-1 rounded-md bg-rose-50 border border-rose-100 text-rose-800 px-2 py-1 text-xs font-medium hover:bg-rose-100 transition-colors"
                                  data-testid={`baking-chip-${bi.id}`}
                                  title="Åpne oppskrift"
                                >
                                  <CakeSlice className="h-3 w-3" /> {name}
                                </Link>
                              );
                            }
                            return (
                              <Link
                                key={bi.id}
                                to="/baking"
                                className="inline-flex items-center gap-1 rounded-md bg-stone-50 border border-stone-100 text-stone-700 px-2 py-1 text-xs font-medium hover:bg-stone-100 transition-colors"
                                data-testid={`baking-chip-${bi.id}`}
                                title="Gå til bakeplanen"
                              >
                                <CakeSlice className="h-3 w-3" /> {name}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-100 px-4 py-3 z-50 shadow-lg">
          <div className="max-w-4xl mx-auto flex items-center gap-3 justify-between">
            <span className="text-xs text-stone-500">Ulagrede endringer</span>
            <button
              onClick={savePlan}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="save-plan-btn"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Lagrer..." : "Lagre matplan"}
            </button>
          </div>
        </div>
      )}

      {/* Recipe/custom picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md" data-testid="picker-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {pickerDay && pickerMeal ? `${MEALS.find((x) => x.id === pickerMeal)?.label} · ${DAY_NAMES[DAY_KEYS.indexOf(pickerDay)]}` : "Legg til måltid"}
            </DialogTitle>
            <DialogDescription>Velg oppskrift eller skriv fritekst.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="f.eks. Pizza, taco, restemat..."
                className="flex-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                data-testid="picker-custom-input"
                onKeyDown={(e) => e.key === "Enter" && assignCustom()}
              />
              <button
                onClick={assignCustom}
                disabled={!customText.trim()}
                className="rounded-md bg-navy-800 text-white px-3 py-2 text-sm font-semibold hover:bg-navy-900 disabled:opacity-50"
                data-testid="picker-custom-add"
              >
                Legg til
              </button>
            </div>
            <div className="border-t border-stone-100 pt-2">
              <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-2">Eller velg oppskrift ({recipes.length})</p>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {recipes.length === 0 ? (
                  <div className="text-xs text-stone-400 italic py-4 text-center">Ingen oppskrifter ennå.</div>
                ) : recipes.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => assignRecipe(r)}
                    className="w-full text-left p-2 rounded-md hover:bg-stone-50 text-sm text-stone-800"
                    data-testid={`picker-recipe-${r.id}`}
                  >
                    {r.title}
                    {r.category && <span className="ml-2 text-[10px] text-stone-400 uppercase tracking-wider">{r.category}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI meal plan dialog */}
      <AIMealPlanDialog open={aiOpen} onOpenChange={setAiOpen} weekStart={weekStart} onApply={applyAI} />
    </div>
  );
}
